import {
  EMPTY_SELECTION,
  EMPTY_SUMMARY,
  type Circle2,
  type DrilledEdge,
  type Point2,
  type Selection,
  type SelectionMergeMode,
  type SelectionRef,
  type SelectionSummary,
  type Store,
  type ViewportState,
  combineStores,
  commonCircle,
  createLens,
  createStore,
  drillSelection,
  mergeSelection,
  walkChain,
  selectionCoversRef,
  selectionKey,
  selectionOf,
  summarizeSelection,
} from '@frontend/viewer-core';

import { PICK_TOLERANCE_PX } from '../constants';
import { coversWholeLoop } from './coversWholeLoop';
import { createMemoizedRolePolicy } from './rolePolicy';
import {
  NO_PICK,
  type DrawingModel,
  type DxfSessionState,
  type LastPickSummary,
  type MappingAssignments,
  type PickedArc,
  type SelectionGranularity,
  type SelectionManager,
} from './types';

/** True when two picks would highlight and select exactly the same things. */
const isSamePick = (
  a: readonly SelectionRef[],
  b: readonly SelectionRef[],
): boolean =>
  a === b ||
  (a.length === b.length &&
    a.every((ref, index) => selectionKey(ref) === selectionKey(b[index])));

/** Field-wise, so re-measuring an unchanged selection does not re-render the overlay. */
const isSameSummary = (a: SelectionSummary, b: SelectionSummary): boolean =>
  a.count === b.count && a.length === b.length && a.area === b.area;

const isSameLastPickSummary = (
  a: LastPickSummary | null,
  b: LastPickSummary | null,
): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.length === b.length &&
    a.entityType === b.entityType &&
    a.arc?.diameter === b.arc?.diameter &&
    a.arc?.sweep === b.arc?.sweep);

/**
 * How far apart the circles of a pick's chunks may be, as a fraction of the
 * radius, and still be one circle. The scene's circles are rounded the same way
 * as its points, so arcs of one circle agree to far closer than this.
 */
const PICK_CIRCLE_FRACTION = 0.01;

/**
 * The circle `picked` runs round, and how far, or `null` unless every chunk of
 * it lies on that one circle.
 *
 * The sweep is the pick's length over the radius rather than the angle between
 * its ends: the scene's lengths are measured from the source entities, while
 * its points are flattened and rounded. It is
 * capped at a whole turn, which a connected pick of one circle never passes.
 */
const pickedArc = (
  picked: readonly SelectionRef[],
  length: number,
  model: DrawingModel,
): PickedArc | null => {
  const circles: Circle2[] = [];
  let isAllArcs = true;
  picked.forEach((ref) => {
    const loop = model.loopsById.get(ref.loopId);
    if (!loop) {
      return;
    }
    const chunks =
      ref.kind === 'edge' ? [loop.chunks[ref.chunkIndex]] : loop.chunks;
    chunks.forEach((chunk) => {
      const circle = chunk && model.arcCircleAt(loop.id, chunk.index);
      if (circle) {
        circles.push(circle);
      } else {
        isAllArcs = false;
      }
    });
  });
  const circle = isAllArcs && commonCircle(circles, PICK_CIRCLE_FRACTION);
  if (!circle || circle.radius <= 0) {
    return null;
  }
  return {
    diameter: 2 * circle.radius,
    sweep: Math.min(length / circle.radius, 2 * Math.PI),
  };
};

/**
 * The DXF type of the one source entity behind `picked`, or `null` when it spans
 * more than one.
 *
 * Read from `entityTypesByKey`, which holds a set because one key can cover
 * several rows; a key whose rows disagree on type has no single answer either.
 */
const pickedEntityType = (
  picked: readonly SelectionRef[],
  model: DrawingModel,
): string | null => {
  const entityIds = new Set<string>();
  picked.forEach((ref) => {
    const loop = model.loopsById.get(ref.loopId);
    if (!loop) {
      return;
    }
    const chunks =
      ref.kind === 'edge' ? [loop.chunks[ref.chunkIndex]] : loop.chunks;
    chunks.forEach((chunk) => {
      if (chunk) {
        entityIds.add(chunk.entityId);
      }
    });
  });
  if (entityIds.size !== 1) {
    return null;
  }
  const [entityId] = entityIds;
  const types = model.entityTypesByKey.get(entityId);
  return types?.size === 1 ? [...types][0] : null;
};

export const createSelectionSlice = (
  state: Store<DxfSessionState>,
): Store<Selection> =>
  createLens(
    state,
    (current) => current.selection,
    (current, selection) => ({ ...current, selection }),
  );

export const createHoveredSlice = (
  state: Store<DxfSessionState>,
): Store<readonly SelectionRef[]> =>
  createLens(
    state,
    (current) => current.hovered,
    (current, hovered) => ({ ...current, hovered }),
  );

type Dependencies = {
  /** Shared with `layerController` — hiding a layer prunes through here too. */
  selection: Store<Selection>;
  hovered: Store<readonly SelectionRef[]>;
  getHiddenLayers: () => ReadonlySet<number>;
  /**
   * The mapping in force, or `null` on a file with none.
   *
   * A getter rather than a snapshot: loop chaining is recomputed against it on
   * every click, which is the whole reason the model is not re-derived when a
   * role changes.
   */
  getAssignments: () => MappingAssignments | null;
  /**
   * What a click picks right now.
   *
   * A getter rather than the store, deliberately: this is where the Alt
   * modifier's temporary inversion will be layered in, and reading through a
   * function means that arrives without this controller — or anything that calls
   * `pickAt` — changing at all.
   */
  getGranularity: () => SelectionGranularity;
  getModel: () => DrawingModel | null;
  viewport: {
    getState(): ViewportState;
    screenToWorld(screen: Point2): Point2;
  };
};

/**
 * What is picked, hovered, and drilled into.
 *
 * Owns `drilledEdge` itself rather than exposing it: it is not one of the
 * session's stores because nothing outside this file needs to react to it,
 * only to the rule it enforces — every entry point that can leave it stale
 * (switching selection, clearing, loading a new drawing) goes through this
 * controller's own methods instead of a shared mutable cell.
 *
 * Owns `lastPick` for the same reason. It is surfaced only measured, through
 * `lastPickSummary`.
 */
export const createSelectionController = ({
  selection,
  hovered,
  getHiddenLayers,
  getAssignments,
  getGranularity,
  getModel,
  viewport,
}: Dependencies): SelectionManager => {
  let drilledEdge: DrilledEdge = null;
  const getRolePolicy = createMemoizedRolePolicy();
  /**
   * What the most recent selecting gesture took, or empty.
   *
   * Not every write to the selection comes through here — the layer and mapping
   * panels, hiding a layer, and mapping undo all write the shared slice directly
   * — so this is never trusted on its own: `lastPickSummary` reports it only
   * while the selection still covers all of it. That one check is what makes a
   * deselect, a subtract, or a panel replacing the selection drop it.
   */
  const lastPick = createStore<readonly SelectionRef[]>(NO_PICK);

  const chainKeyOf = (model: DrawingModel) =>
    getRolePolicy({
      model,
      assignments: getAssignments(),
      hiddenLayers: getHiddenLayers(),
    }).chainKeyOf;

  /** The nearest geometry to a screen point, with the chunk it belongs to. */
  const hitAt = (screenPoint: Point2) => {
    const model = getModel();
    if (!model) {
      return null;
    }
    const { scale } = viewport.getState();
    return model.index.pickAt(viewport.screenToWorld(screenPoint), {
      tolerance: PICK_TOLERANCE_PX / (scale || 1),
      hiddenLayers: getHiddenLayers(),
    });
  };

  const endDrillIfUncovered = (refs: Iterable<SelectionRef>) => {
    const drilled = drilledEdge;
    if (!drilled) {
      return;
    }
    for (const ref of refs) {
      if (
        ref.kind === 'edge' &&
        ref.loopId === drilled.loopId &&
        ref.chunkIndex === drilled.chunkIndex
      ) {
        return;
      }
    }
    // Selecting elsewhere ends the drill: a later double-click there should
    // open that edge, not jump back out to a loop the user has left behind.
    drilledEdge = null;
  };

  const set = (refs: readonly SelectionRef[]) => {
    endDrillIfUncovered(refs);
    selection.setState(() => selectionOf(refs));
    lastPick.setState(() => (refs.length === 0 ? NO_PICK : refs));
  };

  const merge = (refs: readonly SelectionRef[], mode: SelectionMergeMode) => {
    if (mode === 'replace') {
      set(refs);
      return;
    }
    const loops = getModel()?.loops;
    selection.setState((previous) =>
      mergeSelection(previous, refs, mode, loops),
    );
    endDrillIfUncovered(selection.getState().values());
    // A subtract picks nothing new, so the previous pick stands — and drops out
    // of `lastPickSummary` on its own if this took it away. A toggle that
    // deselected is recorded all the same, and drops out the same way.
    if (mode !== 'subtract' && refs.length > 0) {
      lastPick.setState(() => refs);
    }
  };

  const summary = combineStores(
    [selection],
    (): SelectionSummary => {
      const model = getModel();
      return model
        ? summarizeSelection(
            selection.getState(),
            model.loops,
            model.chunkGraph,
            chainKeyOf(model),
          )
        : EMPTY_SUMMARY;
    },
    isSameSummary,
  );

  const lastPickSummary = combineStores(
    [selection, lastPick],
    (): LastPickSummary | null => {
      const model = getModel();
      const picked = lastPick.getState();
      if (!model || picked.length === 0) {
        return null;
      }
      const current = selection.getState();
      // Handed only the ref's own loop: `selectionCoversRef` searches the list
      // it is given, and a marquee pick can hold thousands of refs.
      const isStillSelected = picked.every((ref) => {
        const loop = model.loopsById.get(ref.loopId);
        return selectionCoversRef(current, ref, loop && [loop]);
      });
      if (!isStillSelected) {
        return null;
      }
      // One gesture is one thing — an edge, a loop, or a chain — unless it was a
      // marquee that caught several, which has no single "last" to report.
      const { count, length } = summarizeSelection(
        selectionOf(picked),
        model.loops,
        model.chunkGraph,
        chainKeyOf(model),
      );
      if (count !== 1) {
        return null;
      }
      return {
        length,
        entityType: pickedEntityType(picked, model),
        arc: pickedArc(picked, length, model),
      };
    },
    isSameLastPickSummary,
  );

  return {
    getState: selection.getState,
    subscribe: selection.subscribe,
    set,
    merge,
    clear() {
      drilledEdge = null;
      lastPick.setState(() => NO_PICK);
      selection.setState(() => EMPTY_SELECTION);
      hovered.setState((previous) =>
        previous.length === 0 ? previous : NO_PICK,
      );
    },

    setHovered(refs) {
      // Compared by key rather than by reference: `pickAt` builds a fresh array
      // on every pointer move, so without this the accent overlay would rebuild
      // its buffers for every mouse event over unchanged geometry.
      hovered.setState((previous) =>
        isSamePick(previous, refs) ? previous : refs,
      );
    },

    pickAt(screenPoint) {
      const hit = hitAt(screenPoint);
      if (!hit) {
        return NO_PICK;
      }
      // Under edge granularity a click takes exactly the run the pointer is
      // over, and nothing else has to be worked out.
      if (getGranularity() === 'edge') {
        return [
          { kind: 'edge', loopId: hit.loopId, chunkIndex: hit.chunkIndex },
        ];
      }

      // Under loop granularity it takes the role-aware chain through that run:
      // as far as the geometry goes without changing role and without an
      // ambiguous junction. That is *not* the same as the stitched loop the hit
      // landed in — a role boundary splits one, and a junction the stitcher had
      // to stop at can join two — so the answer is spelled out as the chunks it
      // actually covers rather than named by a single `loopId`.
      const model = getModel();
      if (!model) {
        return NO_PICK;
      }
      const chain = walkChain(
        model.chunkGraph,
        { loopId: hit.loopId, chunkIndex: hit.chunkIndex },
        {
          keyOf: getRolePolicy({
            model,
            assignments: getAssignments(),
            hiddenLayers: getHiddenLayers(),
          }).chainKeyOf,
        },
      );

      // The common case by far: the chain is the whole of one stitched loop, so
      // it collapses back to a single loop ref. Worth detecting rather than
      // always emitting chunks, because a loop ref is what carries area and a
      // count of one into the details overlay — and on an unmapped drawing every
      // chain is this case.
      return coversWholeLoop(model, chain)
        ? [{ kind: 'loop', loopId: hit.loopId }]
        : chain.map(({ loopId, chunkIndex }) => ({
            kind: 'edge' as const,
            loopId,
            chunkIndex,
          }));
    },

    drillAt(screenPoint, mode = 'replace') {
      const hit = hitAt(screenPoint);
      if (!hit) {
        return;
      }
      if (mode !== 'replace') {
        const edgeRef = {
          kind: 'edge' as const,
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        };
        drilledEdge = {
          loopId: hit.loopId,
          chunkIndex: hit.chunkIndex,
        };
        merge([edgeRef], mode);
        return;
      }
      const { ref, drilled } = drillSelection(hit, drilledEdge);
      drilledEdge = drilled;
      set([ref]);
    },

    summary,
    lastPickSummary,
  };
};
